// externals
import type { FocusEvent } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import type { InterstateOnChangeEvent } from '@interstate/components/InterstateEvents';
import * as compositeSelectors from '@makemydeal/dr-dash-store/selectors/compositeSelectors';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as paymentInputActionCreators from '@makemydeal/dr-dash-store/actions/paymentInput';
import { dealerSelectors, featureToggleSelectors } from '@makemydeal/dr-shared-store';

// hooks
import { dealLockHooks } from '../../../hooks';

// utils
import { formatNumberAndCents, isValidNumber, kebabCase } from '@makemydeal/dr-common-utils';
import { convertToNumberOrNull } from '../../../utils/formatUtils';
import { usePaymentCall } from '../../../utils/usePaymentCall';

// consts/enums
import * as offerActionTypes from '@makemydeal/dr-dash-store/actionTypes/offerActionTypes';
import type { StateTree } from '@makemydeal/dr-dash-types';
import { CASH, FINANCE, LEASE } from '@makemydeal/dr-platform-types';
import { DealTabs } from '@makemydeal/dr-shared-types';
import { CASH_DOWN, GENERIC_LAST_FIELD_CHANGED_ERROR } from '../../../constants';
import { generateCashDownLabel } from '../utils';

export const useCashDownField = () => {
    const dispatch = useDispatch();

    const [inputError, setInputError] = useState('');
    const focusValueRef = useRef<number | null>(null);
    const offerType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const fieldKey = kebabCase(CASH_DOWN);
    const isFlexBuyPayment = useSelector(offerReduxSelectors.isFlexBuyPayment);
    const flexBuyPaymentGridEnabled = useSelector(offerReduxSelectors.getFlexBuyPaymentGridToggle);
    const paymentGridEnabledToggle = useSelector((state: StateTree) => offerReduxSelectors.getPaymentGridToggle(state, offerType));
    const deductDueAtSigning = useSelector(dealerSelectors.getIsDueAtSigninAmountDeduct);
    const isTargetOutOfPocket = useSelector(offerReduxSelectors.getIsTargetOutOfPocket);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isEnhancedDealDetailsEnabled = useSelector(featureToggleSelectors.getEnhancedDealDetails);
    const isTargetOutOfPocketLeaseEnabled = useSelector(featureToggleSelectors.isTargetOutOfPocketLeaseEnabled);
    const isDueAtSigningEnhancementsEnabled = useSelector(featureToggleSelectors.enableDueAtSigningEnhancements);
    const failed = useSelector(compositeSelectors.getLastFailureActionIfError(offerActionTypes.UPDATED_DOWN_PAYMENT_IMMEDIATE));
    const failedMessage = useMemo(() => (failed ? GENERIC_LAST_FIELD_CHANGED_ERROR : undefined), [failed]);
    const isOnePayLease = useSelector(offerReduxSelectors.isOnePayLease);
    const isPaymentGridEnabled = isFlexBuyPayment ? flexBuyPaymentGridEnabled : paymentGridEnabledToggle;
    const isLocked = dealLockHooks.useIsDealLocked();

    const isCashOffer = offerType === CASH;
    const isLeaseOffer = offerType === LEASE;
    const isFinanceOffer = offerType === FINANCE;

    // Finance / Lease: down payment field; PO fires on blur or Enter via usePaymentCall.
    const downPaymentCall = usePaymentCall(offerReduxSelectors.getDownPayment, paymentInputActionCreators.updateDownPayment, {
        formatValueToString: formatNumberAndCents,
        autoSave: !isCashOffer
    });

    // Cash: deal deposit field; PO fires on blur or Enter and forwards as `holdDeposit`
    // in the cash payload (see setCashNode in offer-redux).
    const dealDepositCall = usePaymentCall(offerReduxSelectors.getDealDeposit, paymentInputActionCreators.updateDealDeposit, {
        formatValueToString: formatNumberAndCents,
        autoSave: isCashOffer
    });

    const activeCall = isCashOffer ? dealDepositCall : downPaymentCall;
    const { value: activeValue, handleChange, handleBlur, handleKeyDown } = activeCall;

    useEffect(() => {
        setInputError('');
        focusValueRef.current = null;
    }, [offerType]);

    const label = generateCashDownLabel(
        offerType,
        isOnePayLease ? false : deductDueAtSigning,
        isTargetOutOfPocket,
        isEnhancedDealDetailsEnabled,
        isTargetOutOfPocketLeaseEnabled,
        isDueAtSigningEnhancementsEnabled
    );

    const handleChangeWrapper = (e: InterstateOnChangeEvent<string>) => {
        if (isCashOffer) {
            handleChange(e);
            return;
        }

        if (e.target.value === '') {
            setInputError('Please enter a valid amount');
            return;
        }

        setInputError('');
        handleChange(e);
    };

    const handleOfferType = () => {
        if (isFinanceOffer) dispatch(interactionAnalyticsActionCreators.financeCashDownTextInput());
        else if (isLeaseOffer) dispatch(interactionAnalyticsActionCreators.leaseCashDownTextInput());
    };

    const handleBlurWrapper = (e: InterstateOnChangeEvent<string>) => {
        if (focusValueRef.current !== null) {
            const blurredValue = convertToNumberOrNull(e.target.value);
            const valueChanged = blurredValue !== focusValueRef.current;

            if (valueChanged) {
                handleBlur(e);
                handleOfferType();
            }
        }

        focusValueRef.current = null;
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
        focusValueRef.current = convertToNumberOrNull(e.target.value);
    };

    const errorState = inputError || failedMessage;
    const showError = Boolean(inputError || failed);

    const displayTargetOutOfPocket = isTargetOutOfPocketLeaseEnabled && isLeaseOffer && !isDueAtSigningEnhancementsEnabled;

    const parsedActiveValue = parseFloat(String(activeValue).replace(/,/g, ''));
    const isActiveValueValid = isValidNumber(parsedActiveValue);

    const value = isCashOffer ? activeValue : inputError ? '' : isActiveValueValid ? activeValue : '';
    const hasError = isCashOffer ? false : showError;
    const errorMessage = isCashOffer ? '' : errorState;

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled) {
            dispatch(dealScreenErrorActionCreators.setFieldError(DealTabs.SUMMARY, fieldKey, hasError));
        }
    }, [fieldKey, hasError, isDealScreenRedDotErrorsEnabled, dispatch]);

    return {
        label,
        showTargetOutOfPocket: !isCashOffer && displayTargetOutOfPocket,
        hasAdjustmentInputError: showError,
        inputProps: {
            placeholder: 'e.g. 2000.00',
            name: fieldKey,
            value,
            onChange: handleChangeWrapper,
            onFocus: handleFocus,
            onBlur: handleBlurWrapper,
            hasError,
            errorMessage,
            'data-testid': fieldKey,
            allowDecimal: true,
            readOnly: isPaymentGridEnabled,
            decimalMaxLength: 2,
            decimalMinLength: 0,
            inputPrefix: '$',
            autoInsertCommas: true,
            size: 'sm' as const,
            onKeyDown: handleKeyDown,
            disabled: isLocked
        }
    };
};
